import { Uuid } from '@edgebox/data-definition-kit';
import { ClientProjectEntity, FeatureSummary } from '@edgebox/sync-core-rest-client';
import React, { PropsWithChildren } from 'react';
import { AppParamsContext, IAppParams } from '../../AppParams';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { FEATURE_BRAND_PRESENCE_ONBOARDING_COMPLETED, FEATURE_BRAND_PRESENCE_ONBOARDING_INDEXING_DEFERRED } from '../../../features';
import { getCurrentProjectUuid } from '../../../services/SyncCoreApiComponent';

/** The indexing choice the user made on the last step of the onboarding wizard. */
export type BrandPresenceOnboardingRecordingMode = 'index-all' | 'choose' | 'later';

/**
 * Whether the area knows what the project has been through.
 *
 * `unresolved` and `failed` are not "the wizard has not been finished": the area renders only what
 * holds in every state, and never invents an answer out of a read it does not have.
 */
export type BrandPresenceOnboardingStatus = 'unresolved' | 'resolved' | 'failed';

/**
 * Why the area has no answer. `read` is a request that did not come back and is worth another try;
 * `session-project` is the session naming a different project than the one in view, which no retry
 * can change.
 */
export type BrandPresenceOnboardingFailure = 'read' | 'session-project';

export interface IBrandPresenceOnboardingContextValue {
  status: BrandPresenceOnboardingStatus;
  /** Why there is no answer, whenever `status` is `failed`. */
  failure?: BrandPresenceOnboardingFailure;
  /** True only once the flags resolved and the wizard has been finished for this project. */
  isOnboardingCompleted: boolean;
  /** True only once the flags resolved and the wizard was finished without starting the indexing. */
  isIndexingDeferred: boolean;
  project?: ClientProjectEntity;
  /**
   * True when the project read did not come back. It is deliberately not the area's `failed` status:
   * the project is one screen's input, not the whole area's, so a read that fails is a notice a
   * screen can show and `retry` can clear, never a reason to close the area.
   */
  isProjectReadFailed: boolean;
  /** Set while the last recording attempt is unaccounted for, carrying the mode it was made with. */
  failedRecordingMode?: BrandPresenceOnboardingRecordingMode;
  /** True while a recording attempt is in flight, so the control that started it can say so. */
  isRecording: boolean;
  recordOnboardingCompletion: (mode: BrandPresenceOnboardingRecordingMode) => void;
  dismissFailedRecording: () => void;
  adoptProject: (project: ClientProjectEntity) => void;
  retry: () => void;
}

const UNRESOLVED_CONTEXT: IBrandPresenceOnboardingContextValue = {
  status: 'unresolved',
  isOnboardingCompleted: false,
  isIndexingDeferred: false,
  isProjectReadFailed: false,
  isRecording: false,
  recordOnboardingCompletion: () => undefined,
  dismissFailedRecording: () => undefined,
  adoptProject: () => undefined,
  retry: () => undefined,
};

export const BrandPresenceOnboardingContext: React.Context<IBrandPresenceOnboardingContextValue> =
  React.createContext<IBrandPresenceOnboardingContextValue>(UNRESOLVED_CONTEXT);

export function useBrandPresenceOnboarding(): IBrandPresenceOnboardingContextValue {
  return React.useContext(BrandPresenceOnboardingContext);
}

/**
 * Resolves the two onboarding flags and the project once for the whole Brand Presence area, and owns
 * the write that records the wizard's outcome.
 *
 * It is mounted at the area's parent route, above both the navigation and the wizard, for two
 * reasons: a second reader would issue a second project request, because the client's cache fills
 * only once the first response resolves; and the recording write outlives the step that starts it,
 * so its outcome lands even though the user has already moved on.
 */
export function BrandPresenceOnboardingProvider(props: PropsWithChildren) {
  const apiContext = React.useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const projectUuid = appParams?.pageOptions?.projectUuid as Uuid | undefined;
  const sessionJwt = appParams?.siteOptions?.jwt;

  const [status, setStatus] = React.useState<BrandPresenceOnboardingStatus>('unresolved');
  const [failure, setFailure] = React.useState<BrandPresenceOnboardingFailure | undefined>(undefined);
  const [isRecording, setIsRecording] = React.useState(false);
  const [flags, setFlags] = React.useState<FeatureSummary | undefined>(undefined);
  const [project, setProject] = React.useState<ClientProjectEntity | undefined>(undefined);
  const [isProjectReadFailed, setIsProjectReadFailed] = React.useState(false);
  const [failedRecordingMode, setFailedRecordingMode] = React.useState<BrandPresenceOnboardingRecordingMode | undefined>(undefined);
  const [reloadCount, setReloadCount] = React.useState(0);
  // Bumped by every write. A read that started before a write must not put the pre-write answer back
  // once it arrives, or finishing the wizard would be undone by a request that was already in the air.
  const flagsGenerationRef = React.useRef(0);
  // Read synchronously by the retry, which stays enabled so it keeps the focus it was given.
  const isRecordingRef = React.useRef(false);

  // The flag summary and every flag write resolve against the project of the session the area runs
  // under, not against the project the host page put in view. They are the same project by
  // construction — the host page mints a project-scoped session — so a difference means the flags
  // being read and written belong to another project, and acting on them would write the wrong row.
  const sessionProjectUuid = React.useMemo(() => {
    try {
      return getCurrentProjectUuid(sessionJwt);
    } catch (error) {
      // A session whose payload cannot be read proves nothing either way, so the check abstains
      // rather than closing the area on a token it simply could not parse.
      console.error('Failed to read the project of the Brand Presence session', error);
      return undefined;
    }
  }, [sessionJwt]);
  const isProjectMismatch = !!sessionProjectUuid && !!projectUuid && sessionProjectUuid !== projectUuid;

  React.useEffect(() => {
    if (!api) {
      return undefined;
    }

    if (isProjectMismatch) {
      console.error('The Brand Presence session belongs to a different project than the one in view');
      setFailure('session-project');
      setStatus('failed');
      return undefined;
    }

    let active = true;
    const generation = flagsGenerationRef.current;
    // Only the first read has nothing to show. A re-read keeps the answer it has until the new one
    // arrives, or the tab row would empty and refill under whoever is looking at it.
    setStatus((current) => (current === 'resolved' ? current : 'unresolved'));
    setFailure(undefined);

    void (async () => {
      try {
        const summary = await api.utility.features.summary();
        if (!active || generation !== flagsGenerationRef.current) {
          return;
        }

        setFlags(summary);
        setStatus('resolved');
      } catch (error) {
        if (!active || generation !== flagsGenerationRef.current) {
          return;
        }

        console.error('Failed to read the Brand Presence onboarding state', error);
        setFailure('read');
        setStatus('failed');
      }
    })();

    return () => {
      active = false;
    };
  }, [api, projectUuid, isProjectMismatch, reloadCount]);

  React.useEffect(() => {
    if (!api || !projectUuid || isProjectMismatch) {
      return undefined;
    }

    let active = true;
    // A fresh attempt owns its own outcome: clear the last failure so the notice the screen shows
    // tracks this read, and a retry does not keep flagging a read that has already come back.
    setIsProjectReadFailed(false);

    void (async () => {
      try {
        const loadedProject = await api.service.projects.itemByUuid(projectUuid);
        if (!active) {
          return;
        }

        setProject(loadedProject);
      } catch (error) {
        if (!active) {
          return;
        }

        // A screen reads the project to decide a notice it shows only when it knows the notice
        // applies. A read that did not come back leaves that unknown, so the screen says so and
        // offers the retry rather than resolving the unknown into a project whose crawling is on.
        console.error('Failed to load the project of the Brand Presence area', error);
        setIsProjectReadFailed(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [api, projectUuid, isProjectMismatch, reloadCount]);

  const recordOnboardingCompletion = React.useCallback(
    (mode: BrandPresenceOnboardingRecordingMode) => {
      if (!api || isProjectMismatch) {
        setFailedRecordingMode(mode);
        return;
      }

      if (isRecordingRef.current) {
        return;
      }

      isRecordingRef.current = true;
      setIsRecording(true);
      flagsGenerationRef.current += 1;

      void (async () => {
        // Whatever the last successful write answered is the truth about the project, even when the
        // write after it failed: half a recording is still a recording, and throwing its answer away
        // would tell the user that nothing landed when something did.
        let latestSummary: FeatureSummary | undefined;

        try {
          latestSummary = await api.utility.features.update(FEATURE_BRAND_PRESENCE_ONBOARDING_COMPLETED, 'project', 1);

          if (mode === 'later') {
            latestSummary = await api.utility.features.update(FEATURE_BRAND_PRESENCE_ONBOARDING_INDEXING_DEFERRED, 'project', 1);
          }

          setFailedRecordingMode(undefined);
        } catch (error) {
          console.error('Failed to record the Brand Presence onboarding outcome', error);
          setFailedRecordingMode(mode);
        } finally {
          isRecordingRef.current = false;
          setIsRecording(false);

          if (latestSummary) {
            setFlags(latestSummary);
            setStatus('resolved');
            setFailure(undefined);
          }
        }
      })();
    },
    [api, isProjectMismatch]
  );

  const dismissFailedRecording = React.useCallback(() => setFailedRecordingMode(undefined), []);
  const adoptProject = React.useCallback((adopted: ClientProjectEntity) => setProject(adopted), []);
  const retry = React.useCallback(() => setReloadCount((count) => count + 1), []);

  const value = React.useMemo<IBrandPresenceOnboardingContextValue>(
    () => ({
      status,
      failure,
      isOnboardingCompleted: status === 'resolved' && !!flags?.[FEATURE_BRAND_PRESENCE_ONBOARDING_COMPLETED],
      isIndexingDeferred: status === 'resolved' && !!flags?.[FEATURE_BRAND_PRESENCE_ONBOARDING_INDEXING_DEFERRED],
      project,
      isProjectReadFailed,
      failedRecordingMode,
      isRecording,
      recordOnboardingCompletion,
      dismissFailedRecording,
      adoptProject,
      retry,
    }),
    [
      status,
      failure,
      flags,
      project,
      isProjectReadFailed,
      failedRecordingMode,
      isRecording,
      recordOnboardingCompletion,
      dismissFailedRecording,
      adoptProject,
      retry,
    ]
  );

  return <BrandPresenceOnboardingContext.Provider value={value}>{props.children}</BrandPresenceOnboardingContext.Provider>;
}
